import { getCookie } from 'typescript-cookie';
import {
    Row,
    Col,
    Form,
    Space,
    Alert,
    Input,
    Select,
    Typography,
    notification,
    Modal,
    Button,
} from 'antd';
import { BsLightbulbFill } from 'react-icons/bs';
import { FC, useEffect, useState } from 'react';
import { IoIosCloseCircle } from 'react-icons/io';
import { Trans, useTranslation } from 'react-i18next';
import { ESession } from '../../enums/auth.enum';
import { CURRENCIES } from '../../const/currencies.constants';
import { ECountryCodes } from '../../enums/country-code.enum';
import { PhoneNumber } from '../../widgets/PhoneNumber';
import { useAuthStore } from '../../store/auth.store';
import { useWorkspaceStore } from '../../store/workspace.store';
import { addDriver } from '../../api/drivers.api';
import { getErrorMessage } from '../../utils/messages';

const { Option } = Select;
const { Text } = Typography;

interface AddDriverProps {
    open: boolean;
    refreshData: () => void;
    onCancel: () => void;
    handleAddDriver: (driverID: string) => void;
}

const HelpTag: FC = () => {
    const { t } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });
    return (
        <div className="help">
            <div className="help__tag">
                <BsLightbulbFill color="var(--color-dark)" size={12} style={{ verticalAlign: 'middle' }} />
                {t('HELP_TAG')}
            </div>
            <Text>
                <Trans
                    t={t}
                    i18nKey="ADD_DRIVER_HELP_DESCRIPTION"
                    components={{ b: <b /> }}
                />
            </Text>
        </div>
    );
};

const AddDriver: FC<AddDriverProps> = ({
    open, refreshData, onCancel, handleAddDriver,
}) => {
    const { t: ca } = useTranslation('onboarding', { keyPrefix: 'CREATE_ACCOUNT' });
    const { t: tr } = useTranslation('shared', { keyPrefix: 'RESPONSE_ACTIONS' });
    const { t: td } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });

    const user = useAuthStore((state) => state.data);
    const workspace = useWorkspaceStore((state) => state.data);

    const [form] = Form.useForm();
    const [defaultCountryCode, setDefaultCountryCode] = useState<string>(
        /* @ts-ignore */
        `+${ECountryCodes[user.countryCode] || '+593'}`,
    );
    const [error, setError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);

    const onSubmit = () => {
        setLoading(true);

        const payload = {
            ...form.getFieldsValue(),
            language: form.getFieldValue('language').toLowerCase(),
            vehicleYear: Number(form.getFieldValue('vehicleYear')),
            deliveryProvider: workspace?.keyName,
        };
        const token = getCookie(ESession.JWT) || '';

        addDriver(payload, token)
            .then((response) => {
                notification.success({
                    message: td('ADD_DRIVER'),
                    description: td('DRIVER_ADDED'),
                });
                form.resetFields();
                onCancel();
                handleAddDriver(response.data._id as string);
                setTimeout(() => {
                    refreshData();
                }, 1000);
            }).catch((error: any) => {
                const message = getErrorMessage(error.error);
                setError(td(message));
            }).finally(() => setLoading(false));
    };

    const fieldProps = {
        label: ca('PHONE'),
        keyCountryCode: 'countryCode',
        keyMobile: 'mobile',
    };

    const handleSelect = (value: string) => {
        form.setFieldValue('countryCode', value);
        setDefaultCountryCode(value);
    };

    useEffect(() => {
        const code = ECountryCodes[user?.countryCode as keyof typeof ECountryCodes] || '593';
        handleSelect(`+${code}`);
    }, [user]);

    return (
        <Modal
            centered
            open={open}
            onCancel={onCancel}
            title={td('ADD_DRIVER')}
            footer={null}
            width={1000}
            closeIcon={<IoIosCloseCircle size={20} color="var(--color-dark-2)" style={{ verticalAlign: 'middle' }} />}
            style={{ borderRadius: 16 }}
            styles={{ body: { padding: 16 } }}
        >
            <Space direction="vertical" size={16}>
                <HelpTag />
                <Form
                    form={form}
                    onFinish={onSubmit}
                    layout="vertical"
                    initialValues={{
                        language: 'ES',
                        currency: 'USD',
                        vehicleType: 'BIKE',
                        countryCode: `+${ECountryCodes[user?.countryCode as keyof typeof ECountryCodes] || '593'}`,
                    }}
                >
                    {
                        error && (
                            <Alert message={error} type="error" style={{ marginBottom: 16 }} />
                        )
                    }
                    <Row align="top" justify="space-between" gutter={[16, 16]}>
                        <Col xs={24} xxl={12}>
                            <Row align="top" justify="space-between" gutter={[24, 20]}>
                                <Col span={12}>
                                    <Form.Item
                                        name="name"
                                        label={ca('FIRST_NAME')}
                                        rules={[{ required: true, message: tr('REQUIRED') }]}
                                    >
                                        <Input
                                            placeholder={ca('FIRST_NAME')}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="email"
                                        label={ca('EMAIL')}
                                        rules={[{ type: 'email', message: tr('REQUIRED'), required: true }]}
                                    >
                                        <Input
                                            placeholder={ca('EMAIL')}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="language"
                                        label={td('LANGUAGE')}
                                        rules={[{ required: true, message: tr('REQUIRED') }]}
                                    >
                                        <Select className="signup-select">
                                            <Option value="ES">
                                                {td('SPANISH')}
                                            </Option>
                                            <Option value="EN">
                                                {td('ENGLISH')}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col span={12}>
                                    <Form.Item
                                        name="lastName"
                                        label={ca('LAST_NAME')}
                                        rules={[{ required: true, message: tr('REQUIRED') }]}
                                    >
                                        <Input
                                            placeholder={ca('LAST_NAME')}
                                        />
                                    </Form.Item>

                                    <PhoneNumber
                                        form={form}
                                        fieldProps={fieldProps}
                                        handleSelect={handleSelect}
                                        defaultCountryCode={defaultCountryCode}
                                    />

                                    <Form.Item
                                        name="currency"
                                        label={td('CURRENCY')}
                                        rules={[{ required: true, message: tr('REQUIRED') }]}
                                    >
                                        <Select className="signup-select" options={CURRENCIES} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={24} xxl={12}>
                            <Row align="top" justify="space-between" gutter={[24, 20]}>
                                <Col span={12}>
                                    <Form.Item
                                        name="vehicleModelName"
                                        label={td('VEHICLE_MODEL_NAME')}
                                        rules={[{ required: true, message: tr('REQUIRED') }]}
                                    >
                                        <Input
                                            placeholder={td('VEHICLE_MODEL_NAME')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="MCNumber"
                                        label={td('MC_NUMBER')}
                                        rules={[{ required: true, message: tr('REQUIRED') }]}
                                    >
                                        <Input
                                            placeholder={td('MC_NUMBER')}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row align="top" justify="space-between" gutter={[24, 20]}>
                                <Col span={12}>
                                    <Form.Item
                                        name="vehicleColor"
                                        label={td('VEHICLE_COLOR')}
                                        rules={[{ required: true, message: tr('REQUIRED') }]}
                                    >
                                        <Input
                                            placeholder={td('VEHICLE_COLOR')}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="vehicleType"
                                        label={td('VEHICLE_TYPE')}
                                        rules={[{ required: true, message: tr('REQUIRED') }]}
                                    >
                                        <Select className="signup-select">
                                            <Option value="LITE">{td('CAR')}</Option>
                                            <Option value="BIKE">{td('BIKE')}</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="vehicleYear"
                                        label={td('VEHICLE_YEAR')}
                                        rules={[{ required: true, message: tr('REQUIRED') }]}
                                    >
                                        <Input
                                            placeholder={td('VEHICLE_YEAR')}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                        <Col span={24}>
                            <Row justify="space-between" gutter={[16, 16]}>
                                <Col span={12}>
                                    <Button block type="default" onClick={onCancel}>
                                        {td('CANCEL')}
                                    </Button>
                                </Col>
                                <Col span={12}>
                                    <Button block type="primary" htmlType="submit" loading={loading}>
                                        {td('ADD')}
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Form>
            </Space>
        </Modal>
    );
};

export { AddDriver };
